import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { v4 as uuid } from 'uuid';
import type {
    TimeClockActiveSession,
    TimeClockContext,
    TimeClockLogStatus,
    TimeClockSession,
    TimeClockState,
} from './timeClockTypes';

const TIME_CLOCK_STORAGE_KEY = 'corval-consultant-time-clock';
// `storage` only fires in other tabs; this event keeps instances in the same tab in sync.
const TIME_CLOCK_SYNC_EVENT  = 'corval-time-clock-sync';
const SESSION_RETENTION_MS   = 30 * 24 * 60 * 60 * 1000;
const TICK_INTERVAL_MS       = 1000;

const EMPTY_STATE: TimeClockState = { active: null, sessions: [] };

interface UseLocalTimeClockOptions {
    // Set false when the caller doesn't render the running time, to skip the per-second re-render.
    live?: boolean;
}

export interface UseLocalTimeClockResult {
    active: TimeClockActiveSession | null;
    isRunning: boolean;
    elapsedMs: number;
    sessions: TimeClockSession[];
    todaySessions: TimeClockSession[];
    todayTotalMs: number;
    start: ( context?: TimeClockContext ) => void;
    stop: () => TimeClockSession | null;
    trackOffering: ( context: TimeClockContext ) => void;
    completeActive: ( sessionId: string, status: TimeClockLogStatus ) => void;
    reset: () => void;
    removeSession: ( sessionId: string ) => void;
    setLogStatus: ( sessionId: string, status: TimeClockLogStatus ) => void;
    clearToday: () => void;
}

const isValidState = ( value: unknown ): value is TimeClockState => {
    if( !value || typeof value !== 'object' ) return false;
    const record = value as Record<string, unknown>;
    return Array.isArray( record.sessions ) && ( record.active === null || typeof record.active === 'object' );
};

const pruneSessions = ( sessions: TimeClockSession[], now: number ): TimeClockSession[] =>
    sessions.filter( session => now - new Date( session.endedAt ).getTime() < SESSION_RETENTION_MS );

const readTimeClockState = (): TimeClockState => {
    try {
        const raw = window.localStorage.getItem( TIME_CLOCK_STORAGE_KEY );
        if( !raw ) return EMPTY_STATE;

        const parsed: unknown = JSON.parse( raw );
        if( !isValidState( parsed ) ) return EMPTY_STATE;

        return { active: parsed.active, sessions: pruneSessions( parsed.sessions, Date.now() ) };
    } catch {
        return EMPTY_STATE;
    }
};

const writeTimeClockState = ( state: TimeClockState ) => {
    try {
        window.localStorage.setItem( TIME_CLOCK_STORAGE_KEY, JSON.stringify( state ) );
    } catch {
        // ignore storage errors
    }
    window.dispatchEvent( new Event( TIME_CLOCK_SYNC_EVENT ) );
};

const isSameLocalDay = ( iso: string, reference: Date ): boolean => {
    const date = new Date( iso );
    return date.getFullYear() === reference.getFullYear()
        && date.getMonth() === reference.getMonth()
        && date.getDate() === reference.getDate();
};

const createActiveSession = ( context?: TimeClockContext ): TimeClockActiveSession => ( {
    id: uuid(),
    startedAt: new Date().toISOString(),
    clientId: context?.clientId,
    clientName: context?.clientName,
    clientOfferingId: context?.clientOfferingId,
    offeringName: context?.offeringName,
} );

const finishSession = ( active: TimeClockActiveSession ): TimeClockSession => {
    const endedAt = new Date();
    return {
        ...active,
        endedAt: endedAt.toISOString(),
        durationMs: Math.max( 0, endedAt.getTime() - new Date( active.startedAt ).getTime() ),
    };
};

export const useLocalTimeClock = ( { live = true }: UseLocalTimeClockOptions = {} ): UseLocalTimeClockResult => {
    const [ state, setState ] = useState<TimeClockState>( readTimeClockState ),
          [ now, setNow ]     = useState<number>( () => Date.now() ),
          stateRef            = useRef<TimeClockState>( state );

    const commit = useCallback( ( next: TimeClockState ) => {
        stateRef.current = next;
        setState( next );
        writeTimeClockState( next );
    }, [] );

    useEffect( () => {
        const syncFromStorage = () => {
            const next = readTimeClockState();
            stateRef.current = next;
            setState( next );
            setNow( Date.now() );
        };

        const handleStorage = ( event: StorageEvent ) => {
            if( event.key === TIME_CLOCK_STORAGE_KEY ) syncFromStorage();
        };

        window.addEventListener( 'storage', handleStorage );
        window.addEventListener( TIME_CLOCK_SYNC_EVENT, syncFromStorage );
        return () => {
            window.removeEventListener( 'storage', handleStorage );
            window.removeEventListener( TIME_CLOCK_SYNC_EVENT, syncFromStorage );
        };
    }, [] );

    useEffect( () => {
        if( !state.active || !live ) return;

        setNow( Date.now() );
        const intervalId = window.setInterval( () => setNow( Date.now() ), TICK_INTERVAL_MS );
        return () => window.clearInterval( intervalId );
    }, [ state.active, live ] );

    const start = useCallback( ( context?: TimeClockContext ) => {
        if( stateRef.current.active ) return;
        commit( { ...stateRef.current, active: createActiveSession( context ) } );
    }, [ commit ] );

    const stop = useCallback( (): TimeClockSession | null => {
        const { active, sessions } = stateRef.current;
        if( !active ) return null;

        const finished = finishSession( active );
        commit( { active: null, sessions: [ finished, ...sessions ] } );
        return finished;
    }, [ commit ] );

    // Starts the timer on an offering, or moves a running timer onto it. A timer with no
    // offering yet is adopted whole; one already on another offering is closed out first
    // so time before the switch stays with the offering it was spent on.
    const trackOffering = useCallback( ( context: TimeClockContext ) => {
        const { active, sessions } = stateRef.current;

        if( !active ){
            commit( { ...stateRef.current, active: createActiveSession( context ) } );
            return;
        }

        if( active.clientOfferingId === context.clientOfferingId ) return;

        if( !active.clientOfferingId ){
            commit( {
                ...stateRef.current,
                active: {
                    ...active,
                    clientId: context.clientId ?? active.clientId,
                    clientName: context.clientName ?? active.clientName,
                    clientOfferingId: context.clientOfferingId,
                    offeringName: context.offeringName,
                },
            } );
            return;
        }

        commit( {
            active: createActiveSession( { ...context, clientName: context.clientName ?? active.clientName } ),
            sessions: [ finishSession( active ), ...sessions ],
        } );
    }, [ commit ] );

    // Called after its hours are saved. If the timer was already stopped (e.g. from another tab),
    // the finished session is marked instead.
    const completeActive = useCallback( ( sessionId: string, status: TimeClockLogStatus ) => {
        const { active, sessions } = stateRef.current;

        if( active?.id === sessionId ){
            commit( { active: null, sessions: [ { ...finishSession( active ), logStatus: status }, ...sessions ] } );
            return;
        }

        commit( {
            ...stateRef.current,
            sessions: sessions.map( session => session.id === sessionId ? { ...session, logStatus: status } : session ),
        } );
    }, [ commit ] );

    const reset = useCallback( () => {
        if( !stateRef.current.active ) return;
        commit( { ...stateRef.current, active: null } );
    }, [ commit ] );

    const removeSession = useCallback( ( sessionId: string ) => {
        commit( {
            ...stateRef.current,
            sessions: stateRef.current.sessions.filter( session => session.id !== sessionId ),
        } );
    }, [ commit ] );

    const setLogStatus = useCallback( ( sessionId: string, status: TimeClockLogStatus ) => {
        commit( {
            ...stateRef.current,
            sessions: stateRef.current.sessions.map( session =>
                session.id === sessionId ? { ...session, logStatus: status } : session
            ),
        } );
    }, [ commit ] );

    const clearToday = useCallback( () => {
        const today = new Date();
        commit( {
            ...stateRef.current,
            sessions: stateRef.current.sessions.filter( session => !isSameLocalDay( session.startedAt, today ) ),
        } );
    }, [ commit ] );

    const elapsedMs = state.active
        ? Math.max( 0, now - new Date( state.active.startedAt ).getTime() )
        : 0;

    const todaySessions = useMemo( () => {
        const today = new Date( now );
        return state.sessions.filter( session => isSameLocalDay( session.startedAt, today ) );
    }, [ state.sessions, now ] );

    const todayTotalMs = useMemo(
        () => todaySessions.reduce( ( total, session ) => total + session.durationMs, 0 ),
        [ todaySessions ]
    );

    return {
        active: state.active,
        isRunning: !!state.active,
        elapsedMs,
        sessions: state.sessions,
        todaySessions,
        todayTotalMs,
        start,
        stop,
        trackOffering,
        completeActive,
        reset,
        removeSession,
        setLogStatus,
        clearToday,
    };
};
